import classcat from "classcat";
import React from "react";

import { IconDown } from "@touchtech/web-baselayer-ui";

import {
  AttributeType,
  PreferenceOption,
  SearchTermType,
  SearchTermTypeLabels,
} from "@is/types";

import AttributeOption from "../AttributeOption";
import AttributeTab from "../AttributeTab";
import css from "./AttributeOptions.module.css";
import useManageAttributeOptions from "./hooks/useManageAttributeOptions";

type Props = {
  preferences: PreferenceOption[];
  onPreferenceClick: (preference: PreferenceOption) => void;
  areOptionsExpanded: boolean;
  setAreOptionsExpanded: (value: boolean) => void;
  tabOptions: PreferenceOption[];
  optionsSelectedFromTab: PreferenceOption[];
  isSelected: (option: PreferenceOption) => boolean;
};

const AttributeOptions = ({
  preferences,
  onPreferenceClick,
  areOptionsExpanded,
  setAreOptionsExpanded,
  tabOptions,
  optionsSelectedFromTab,
  isSelected,
}: Props) => {
  const {
    allOptions,
    handleOptionSelect,
    handleExpandOptions,
    showLength,
    hasSelectedSizes,
    lengthOptions,
    subCategories,
    showOther,
    selectedTab,
    t,
  } = useManageAttributeOptions({
    preferences,
    onPreferenceClick,
    areOptionsExpanded,
    setAreOptionsExpanded,
    tabOptions,
    optionsSelectedFromTab,
  });

  return (
    <div className={css.optionsContainer}>
      {allOptions?.length ? (
        <div className={classcat([css.optionsWrapper, css.fadeIn])}>
          {allOptions?.map((option) => (
            <AttributeOption
              key={option.key}
              option={option}
              onOptionSelect={handleOptionSelect}
              isSelected={isSelected(option)}
            />
          ))}
          {showOther && (
            <button
              className={classcat([css.showOther, "text-label-lg"])}
              onClick={handleExpandOptions}
            >
              <span>
                {selectedTab === AttributeType.Sizes
                  ? t("Show other sizes")
                  : t("Show other categories")}
              </span>
              <IconDown className={"text-label-xs"} />
            </button>
          )}
        </div>
      ) : (
        <div className={css.noOptions}>
          <span className="text-label-lg">
            {t("No options that match your selections")}
          </span>
        </div>
      )}
      {showLength && (
        <>
          <div className={classcat([css.subTabContainer, css.fadeIn])}>
            <AttributeTab
              attribute={{
                key: SearchTermType.Length,
                label: SearchTermTypeLabels[SearchTermType.Length],
                options: [],
                type: SearchTermType.Length,
                parentKey: SearchTermType.Length,
              }}
              isSelected
            />
          </div>
          <div className={classcat([css.optionsWrapper, css.fadeIn])}>
            {hasSelectedSizes ? (
              lengthOptions?.map((option) => (
                <AttributeOption
                  key={option.key}
                  option={option}
                  onOptionSelect={handleOptionSelect}
                  isSelected={isSelected(option)}
                />
              ))
            ) : (
              <span
                className={classcat([css.selectSizeFirst, "text-label-lg"])}
              >
                {t("Please select your size first")}
              </span>
            )}
          </div>
        </>
      )}
      {!!subCategories?.length && (
        <>
          <div className={classcat([css.subTabContainer, css.fadeIn])}>
            <AttributeTab
              attribute={{
                key: SearchTermType.Subcategory,
                label: SearchTermTypeLabels[SearchTermType.Subcategory],
                options: [],
                type: SearchTermType.Subcategory,
                parentKey: SearchTermType.Subcategory,
              }}
              isSelected
            />
          </div>
          <div className={classcat([css.optionsWrapper, css.fadeIn])}>
            {subCategories?.map((option, index) => (
              <AttributeOption
                key={option.key + index}
                option={option}
                onOptionSelect={handleOptionSelect}
                isSelected={isSelected(option)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AttributeOptions;
